import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney, formatQty } from '../lib/format.js'
import KitchenLoader from './KitchenLoader.jsx'

// AI Suggest Reorder modal.
//
// Phases:
//   generating — calls suggest-reorder Edge Function
//   review     — show ranked plan, manager picks items / edits qty
//   submitting — sends one PO per supplier group via the existing
//                sendOrder mutator
//   error      — retry path
//
// On accept, we group the selected rows by preferred_supplier_id and
// call onCreateOrders([{ supplier, lineItems }, ...]). The parent
// (ReorderSuggestionsScreen) hands those to the existing sendOrder
// flow so we don't duplicate PO logic.
export default function SuggestReorderModal({
  open,
  onClose,
  items,                 // full active items array (for supplier lookup + cost)
  receiveLogs = [],      // last 30d
  wasteLogs = [],        // last 30d
  itemSales = [],        // last 30d
  suppliers = [],        // for grouping label resolution
  onCreateOrders,        // ([{ supplier, lineItems, notes }, ...]) => Promise
}) {
  const [phase, setPhase]    = useState('generating')
  const [error, setError]    = useState(null)
  const [result, setResult]  = useState(null)
  // Manager's edits, keyed by item_id: { selected: bool, qty: string }
  const [edits, setEdits]    = useState({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setPhase('generating')
    setError(null)
    setResult(null)
    setEdits({})
    let cancelled = false
    ;(async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
        const { data: sess } = await supabase.auth.getSession()
        const token = sess?.session?.access_token || anonKey

        const payload = {
          items: (items || []).map((i) => ({
            id: i.id, name: i.name, category: i.category, unit: i.unit,
            cost_per_unit: Number(i.cost_per_unit || 0),
            par_qty: Number(i.par_qty || 0),
            current_on_hand: Number(i.current_on_hand || 0),
            lead_time_days: Number(i.lead_time_days || 1),
            pack_size: Number(i.pack_size || 1),
            pack_unit: i.pack_unit,
            preferred_supplier_id: i.preferred_supplier?.id ?? null,
            preferred_supplier: i.preferred_supplier,
          })),
          receive_logs: receiveLogs.map((r) => ({
            item_id: r.item_id,
            quantity: Number(r.quantity || 0),
            unit: r.unit,
            logged_at: r.logged_at,
          })),
          waste_logs: wasteLogs.map((w) => ({
            item_id: w.item_id,
            quantity: Number(w.quantity || 0),
            unit: w.unit,
            logged_at: w.logged_at,
          })),
          item_sales: itemSales.map((s) => ({
            item_id: s.item_id,
            quantity_sold: Number(s.quantity_sold || 0),
            period_end: s.period_end,
          })),
          observation_days: 30,
        }

        const resp = await fetch(`${supabaseUrl}/functions/v1/suggest-reorder`, {
          method: 'POST',
          headers: {
            'content-type':  'application/json',
            'authorization': `Bearer ${token}`,
            'apikey':        anonKey,
          },
          body: JSON.stringify(payload),
        })
        if (cancelled) return
        if (!resp.ok) {
          const errText = await resp.text()
          throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
        }
        const data = await resp.json()
        if (data.error) {
          setError(data.message || 'AI could not generate a plan.')
          setPhase('error')
          return
        }
        setResult(data)
        // Pre-select all rankings, default qty to suggested_qty
        const initialEdits = {}
        for (const r of (data.rankings || [])) {
          initialEdits[r.item_id] = {
            selected: true,
            qty: String(r.suggested_qty ?? ''),
          }
        }
        setEdits(initialEdits)
        setPhase('review')
      } catch (e) {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error('[SuggestReorderModal] generate failed', e)
        setError('Could not generate — please try again in a moment.')
        setPhase('error')
      }
    })()
    return () => { cancelled = true }
  }, [open, items, receiveLogs, wasteLogs, itemSales])

  // Lookup map: item_id → catalog item (for supplier + cost)
  const itemsById = useMemo(() => {
    const m = new Map()
    for (const i of items ?? []) m.set(i.id, i)
    return m
  }, [items])

  // Compute selected total + grouped by supplier for the CTA preview
  const previewTotal = useMemo(() => {
    if (!result?.rankings) return { total: 0, count: 0, groups: [] }
    const groupsMap = new Map()
    let total = 0, count = 0
    for (const r of result.rankings) {
      const e = edits[r.item_id]
      if (!e?.selected) continue
      const qty = Number(e.qty || 0)
      if (!(qty > 0)) continue
      const item = itemsById.get(r.item_id)
      const cost = Number(item?.cost_per_unit || 0)
      const lineTotal = qty * cost
      total += lineTotal
      count++
      const supplier = item?.preferred_supplier ?? null
      const supplierKey = supplier?.id ?? '__unassigned'
      const supplierName = supplier?.name ?? 'Unassigned'
      const prev = groupsMap.get(supplierKey) || {
        supplierKey, supplier, supplierName, items: [], subtotal: 0,
      }
      prev.items.push({
        item_id: r.item_id, item_name: r.item_name,
        quantity: qty, unit: r.unit ?? item?.unit ?? '',
        cost_per_unit: cost,
        total_cost: lineTotal,
      })
      prev.subtotal += lineTotal
      groupsMap.set(supplierKey, prev)
    }
    return { total, count, groups: [...groupsMap.values()] }
  }, [result, edits, itemsById])

  if (!open) return null

  function setEdit(itemId, patch) {
    setEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }))
  }

  async function handleAccept() {
    if (submitting) return
    if (previewTotal.count === 0) return
    setSubmitting(true)
    setError(null)
    try {
      // One PO per supplier group, using existing sendOrder under the hood
      const orders = previewTotal.groups.map((g) => ({
        supplier:  g.supplier,
        lineItems: g.items,
        notes:     `AI-suggested reorder · ${new Date().toLocaleDateString()}`,
      }))
      await onCreateOrders(orders)
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SuggestReorderModal] accept failed', e)
      setError(`Could not save: ${e?.message || 'try again.'}`)
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="AI suggest reorder"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">✨ AI Suggest Order</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {phase === 'generating' && (
            <KitchenLoader
              messages={[
                'Checking the walk-in\u2026',
                'Reading 30 days of usage\u2026',
                'Building your shopping list\u2026',
                'Plating it up\u2026',
              ]}
            />
          )}

          {phase === 'error' && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={onClose}
                className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
              >
                Close and try again
              </button>
            </div>
          )}

          {phase === 'review' && result && (
            <>
              {result.notes && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm leading-relaxed text-emerald-900">
                  <div className="text-[11px] font-medium text-emerald-900/70">
                    AI's analysis
                  </div>
                  <div className="mt-1">{result.notes}</div>
                </div>
              )}

              {result.warnings && result.warnings.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm leading-relaxed text-amber-900">
                  <div className="text-[11px] font-medium text-amber-900/70">
                    Heads up
                  </div>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {result.rankings.length === 0 && (
                <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/65">
                  Nothing needs reordering right now. {result.notes ? '' : 'All items above par or velocity too low to flag.'}
                </div>
              )}

              {result.rankings.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[11px] font-medium text-black/60">
                    Recommended ({result.rankings.length})
                  </div>
                  {result.rankings.map((r) => {
                    const item = itemsById.get(r.item_id)
                    const e = edits[r.item_id] || { selected: true, qty: '' }
                    const qty = Number(e.qty || 0)
                    const cost = Number(item?.cost_per_unit || 0)
                    const lineCost = qty * cost
                    const tier = TONES[r.urgency] || TONES.healthy
                    return (
                      <div
                        key={r.item_id}
                        className={`rounded-xl border p-3 ${e.selected ? tier.border : 'border-black/10'} ${e.selected ? tier.bg : 'bg-white'}`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <label className="flex flex-1 items-baseline gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!e.selected}
                              onChange={(ev) => setEdit(r.item_id, { selected: ev.target.checked })}
                              className="h-4 w-4 mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-base font-medium">
                                {r.item_name}
                                <span className={`ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${tier.badge}`}>
                                  {r.urgency}
                                </span>
                              </div>
                              <div className="mt-0.5 truncate text-xs text-black/60">
                                On hand {formatQty(item?.current_on_hand ?? 0, r.unit)} ·
                                par {formatQty(item?.par_qty ?? 0, r.unit)}
                                {r.estimated_days_remaining != null && (
                                  <> · ~{Number(r.estimated_days_remaining).toFixed(1)}d left</>
                                )}
                                {item?.preferred_supplier?.name && (
                                  <> · {item.preferred_supplier.name}</>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>

                        {r.reasoning && (
                          <div className="mt-2 text-xs italic text-black/55">{r.reasoning}</div>
                        )}

                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-black/55">Qty</label>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="any"
                              min="0"
                              value={e.qty}
                              onChange={(ev) => setEdit(r.item_id, { qty: ev.target.value })}
                              disabled={!e.selected}
                              className="w-full rounded-lg border border-black/15 px-2 py-2 text-base font-medium tabular-nums outline-none focus:border-blue-600 disabled:bg-black/[0.04]"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-black/55">Unit</label>
                            <div className="rounded-lg border border-black/10 bg-black/[0.02] px-2 py-2 text-base text-black/70">
                              {r.unit}
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-black/55">Line $</label>
                            <div className="rounded-lg border border-black/10 bg-black/[0.02] px-2 py-2 text-base font-medium tabular-nums text-black/85">
                              {formatMoney(lineCost)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {phase === 'review' && result?.rankings?.length > 0 && (
          <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
            <div className="mb-2 text-xs text-black/60 leading-snug">
              {previewTotal.count > 0 ? (
                <>
                  {previewTotal.count} {previewTotal.count === 1 ? 'item' : 'items'} ·
                  {' '}{previewTotal.groups.length} {previewTotal.groups.length === 1 ? 'supplier' : 'suppliers'} ·
                  {' '}<span className="font-medium text-black/80">{formatMoney(previewTotal.total)}</span>
                </>
              ) : (
                <span className="text-black/45">No items selected</span>
              )}
            </div>
            <button
              type="button"
              onClick={handleAccept}
              disabled={submitting || previewTotal.count === 0}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Creating orders\u2026' : `Create ${previewTotal.groups.length} order${previewTotal.groups.length === 1 ? '' : 's'}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const TONES = {
  'critical': { badge: 'bg-red-600 text-white',         border: 'border-red-300',    bg: 'bg-red-50/40' },
  'soon':     { badge: 'bg-amber-100 text-amber-900',   border: 'border-amber-200',  bg: 'bg-amber-50/40' },
  'healthy':  { badge: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', bg: 'bg-emerald-50/30' },
}
